/**
 * 成员详情。
 *
 * 两个来源共同决定显示什么：
 *   · `visibleFields`：服务端按权限点给出的「健康 / 财富区块是否可见」；
 *   · 字段本身是否存在：无权时服务端根本不发这些字段（裁剪在服务端完成）。
 * 二者一致才渲染真实数据；不一致（或可见但无值）时如实说明原因，
 * **不留空白、不显示 0**。
 *
 * 权益核销是本页的一个区块（`MemberBenefits`）：余额、清零倒计时与核销流水全部来自
 * `/api/admin/members/:id/benefits`，读不到时照实显示服务端 403 原文；
 * 核销动作按权限点 `benefit:consume` 决定是否出现。`permissions` 由 App 从 `/api/admin/me`
 * 传入，不在这里硬编码角色。
 */
import { useNavigate, useParams } from 'react-router-dom';
import { wealthCaliber } from '@hew3/shared';
import { adminApi, ApiError } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { Badge, Card, Kpi, NoPermission, PageHead, PermissionBlock } from '../components/ui.tsx';
import { ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';
import { EMPTY_TEXT, formatDate, formatText, levelLabel, trustPlanLabel } from '../lib/format.ts';
import { MemberBenefits } from './MemberBenefits.tsx';

/** 键值对单元：值可能来自被裁剪的字段，因此一律走「未下发 / 无值」两种文案。 */
function Kv({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="kv-l">{label}</div>
      <div className={`kv-v${mono ? '' : ' text'}`}>{value}</div>
    </div>
  );
}

export function MemberDetailPage({ permissions }: { permissions: string[] }) {
  const params = useParams();
  const navigate = useNavigate();
  const memberId = params.id ?? '';
  const { data, error, loading, reload } = useLoad((signal) => adminApi.member(memberId, signal));

  if (loading) return <LoadingBox text="正在加载成员档案…" />;

  if (error) {
    // 范围外成员返回 403（不是 404）：这是权限拦截，必须与「成员不存在」区分开
    if (error instanceof ApiError && error.status === 403) {
      return (
        <div>
          <PageHead title="无权查看该成员" sub="403 · 权限拦截" />
          <Card>
            <PermissionBlock
              title="该成员不在您的可见范围内"
              desc={`服务端返回：${error.message}。可见范围由守护关系决定 —— 同一角色的两位管家，能看到哪些成员取决于各自被分派的守护小组，而不是角色本身。`}
            />
            <div className="sp12" />
            <button type="button" className="btn" onClick={() => navigate('/members')}>
              返回成员列表
            </button>
          </Card>
        </div>
      );
    }
    if (error instanceof ApiError && error.status === 404) {
      return (
        <div>
          <PageHead title="成员不存在" sub="404" />
          <ErrorBox message="该成员编号在库中不存在，可能已被移除或编号有误。" />
          <div className="sp12" />
          <button type="button" className="btn" onClick={() => navigate('/members')}>
            返回成员列表
          </button>
        </div>
      );
    }
    return <ErrorBox message={errorText(error)} onRetry={reload} />;
  }

  if (!data) return <ErrorBox message="成员档案为空" onRetry={reload} />;

  const member = data.member;
  const healthVisible = data.visibleFields.health;
  const wealthVisible = data.visibleFields.wealth;

  return (
    <div>
      <PageHead
        title={`${member.name}`}
        sub={`成员编号 ${member.no} · 数据来源：平台成员档案库`}
        actions={
          <button type="button" className="btn sm" onClick={() => navigate('/members')}>
            返回成员列表
          </button>
        }
      />

      <Card title="会籍与联络" sub="非敏感字段">
        <div className="kv">
          <Kv label="姓名" value={member.name} />
          <Kv label="成员编号" value={member.no} mono />
          <Kv label="会籍" value={levelLabel(member.level)} />
          <Kv label="城市" value={formatText(member.city)} />
          <Kv label="加入日期" value={formatDate(member.join_date)} mono />
          <Kv
            label="守护小组成员数"
            value={member.guardian_count === undefined ? EMPTY_TEXT : String(member.guardian_count)}
            mono
          />
        </div>
        <div className="divider" />
        <div className="row">
          <span className="muted text-tiny">未解除预警</span>
          {member.openAlerts === undefined ? <NoPermission what="未解除预警数" /> : <Badge tone="warn">{member.openAlerts} 条</Badge>}
          <span className="muted text-tiny">
            （未解除预警数属于健康敏感字段组，无权查看健康数据时服务端同样不下发）
          </span>
        </div>
      </Card>

      <div className="grid2">
        <Card title="生命健康 H" sub="敏感字段 · member:read_health">
          {!healthVisible ? (
            <PermissionBlock
              title="当前角色无权查看健康数据"
              desc="健康分数、生物年龄、器官年龄属于敏感个人信息，仅向具备 member:read_health 权限的角色（健康管家、专家、超级管理员）下发。若因工作需要，请联系超级管理员调整角色或被分派到健康板块守护小组 —— 平台不会用 0 或占位数字替代。"
            />
          ) : (
            <>
              <div className="grid2">
                <Kpi
                  label="健康分（H-Score）"
                  value={member.health_score === undefined ? '—' : String(member.health_score)}
                  tone="w"
                  delta={member.health_grade !== undefined ? `等级 ${member.health_grade}` : '等级未下发'}
                />
                <Kpi label="企业治理指数（E）" value={member.enterpriseScore === null || member.enterpriseScore === undefined ? '—' : String(member.enterpriseScore)} tone="e" delta="非敏感字段，按可见范围下发" />
              </div>
              <div className="sp12" />
              <div className="kv">
                <Kv label="生物年龄" value={member.bio_age === undefined ? '未下发' : `${member.bio_age}`} mono />
                <Kv label="器官年龄" value={member.organ_age === undefined ? '未下发' : `${member.organ_age}`} mono />
                <Kv label="未解除预警" value={member.openAlerts === undefined ? '未下发' : `${member.openAlerts} 条`} mono />
              </div>
              <div className="note-src">
                <b>数据说明</b>
                <br />
                健康分与年龄指标来自平台健康档案的最近一次评估结果。平台不开展临床诊疗，
                医疗判断由具备资质的合作医疗机构给出。
              </div>
            </>
          )}
        </Card>

        <Card title="财富健康 W" sub="敏感字段 · member:read_wealth">
          {!wealthVisible ? (
            <PermissionBlock
              title="当前角色无权查看财富数据"
              desc="财富健康分数与信托方案属于敏感个人信息，仅向具备 member:read_wealth 权限的角色（财务、超级管理员）下发。财务看不到健康、运营看不到健康与财富，这是职责分离的设计要求。"
            />
          ) : (
            <>
              <div className="grid2">
                <Kpi
                  label="财富健康分（W-Score）"
                  value={member.wealth_score === undefined ? '—' : String(member.wealth_score)}
                  tone="w"
                  delta={member.wealth_grade !== undefined ? `等级 ${member.wealth_grade}` : '等级未下发'}
                />
                <Kpi label="信托方案" value={trustPlanLabel(member.trust_plan)} delta={wealthCaliber.source} />
              </div>
              <div className="note-src">
                <b>口径</b>
                <br />
                {wealthCaliber.source}；平台提供 {wealthCaliber.trustPlans} 档健康信托方案。
                信托与法务由持牌合作机构落地，平台不做投资建议。
              </div>
            </>
          )}
        </Card>
      </div>

      {/* 权益核销区块：读不到时（403）区块内部自己照实显示服务端原文，不伪装成空列表 */}
      <MemberBenefits memberId={member.id} permissions={permissions} />

      <div className="note-src">
        <b>审计留痕</b>
        <br />
        您本次查看该成员档案已写入审计链（操作者工号、姓名 + 角色、查看目的「后台查看成员档案」），
        成员可在自己的「数据调用记录」中看到是谁、因为什么查看了他的数据。
      </div>
    </div>
  );
}
